import FormItemPrice from "@/module/FormItemPrice";
import NumberHelper from "@/module/NumberHelper";
import DiscountCode from "@/module/DiscountCode/DiscountCode";
import FormHelper from "@/module/FormHelper";
import ExchangeRateProvider from "@/module/ExchangeRate/ExchangeRateProvider";
import { ItemType } from "@/module/Item/ItemType";
import FormItemMetadata from "@/module/FormItemMetadata";

export default class FormItem {
  readonly id: number;
  readonly itemTemplateId: number;
  readonly disableDiscount: boolean;
  readonly type: ItemType | null;
  readonly reverseCharge: boolean;
  readonly showStockInformation: boolean;
  readonly enableStock: boolean;
  readonly stock: number | null;
  readonly allowChangeCounts: boolean;
  readonly soldOut: boolean;
  readonly prices: FormItemPrice[];
  readonly includingVat: boolean;
  readonly originVat: number;
  readonly name: string;
  readonly description: string | null;
  readonly publicDescription: string | null;
  readonly required: boolean;
  readonly groupId: number | null;
  readonly period: string | null;
  readonly metadata: FormItemMetadata;
  private _currentVat: number;
  private _currentPrice: number = 0.0;
  private _currentPriceIncVat: number = 0.0;
  private _currentDiscountedPriceIncVat: number | null = null;
  private _currentDiscountedPrice: number | null = null;
  private _originalPriceIncVat: number = 0.0;
  private _originalPrice: number = 0.0;
  private _isSpecialPrice: boolean = false;
  private _vatPayer: boolean = false;
  readonly code: string | null;
  readonly imageUrl: string | null;

  constructor(
    id: number,
    itemTemplateId: number,
    prices: FormItemPrice[],
    includingVat: boolean,
    originVat: number | null,
    name: string,
    description: string | null,
    publicDescription: string | null,
    required: boolean,
    disableDiscount: boolean,
    type: ItemType | null,
    reverseCharge: boolean,
    showStockInformation: boolean,
    enableStock: boolean,
    stock: number | null,
    allowChangeCounts: boolean,
    soldOut: boolean,
    groupId: number | null,
    period: string | null,
    metadata: FormItemMetadata,
    code: string | null = null,
    imageUrl: string | null = null,
  ) {
    this.id = id;
    this.itemTemplateId = itemTemplateId;
    this.prices = prices;
    this.includingVat = Boolean(includingVat);
    this.originVat = originVat === null ? 0.0 : Number(originVat);
    this.name = name;
    this.description = description;
    this.publicDescription = publicDescription;
    this.required = required;
    this.disableDiscount = Boolean(disableDiscount);
    this.type = type;
    this.reverseCharge = Boolean(reverseCharge);
    this.showStockInformation = Boolean(showStockInformation);
    this.enableStock = Boolean(enableStock);
    this.stock = stock;
    this.allowChangeCounts = Boolean(allowChangeCounts);
    this.soldOut = Boolean(soldOut);
    this.groupId = groupId === null ? 0 : Number(groupId);
    this._currentVat = this.originVat;
    this.period = period;
    this.metadata = metadata;
    this.code = code;
    this.imageUrl = imageUrl;
  }

  async recalculate(
    vat: number,
    currency: string,
    domesticCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
    vatPayer: boolean,
    useRepayment = false,
    repaymentCount: number | null = null,
  ): Promise<void> {
    this._currentVat = vat;
    this._vatPayer = vatPayer;

    const priceObject = this.findPriceByCurrencyAndType(
      currency,
      useRepayment,
      repaymentCount,
    );
    this.updateOriginalPrice(currency, useRepayment, repaymentCount);
    this.updateIsSpecialPrice(currency, useRepayment);

    if (priceObject !== undefined) {
      this.refreshPrice(priceObject.price);

      return;
    }

    const domesticPriceObject = this.findPriceByCurrencyAndType(
      domesticCurrency,
      useRepayment,
      repaymentCount,
    );

    let domesticPrice = null;

    if (domesticPriceObject !== undefined) {
      domesticPrice = domesticPriceObject.price;
    } else {
      let firstPriceObject: FormItemPrice | null = null;

      for (const tmpPrice of this.prices) {
        if (tmpPrice.type !== "one_time") {
          continue;
        }

        if (!exchangeRateProvider.hasExchangeRate(tmpPrice.currency_code)) {
          continue;
        }

        firstPriceObject = tmpPrice;
        break;
      }

      if (firstPriceObject === null) {
        throw new Error(
          "Invalid item data for item: " + this.code + " name: " + this.name,
        );
      }

      domesticPrice = (await FormHelper.toDomesticCurrency(
        firstPriceObject.price,
        firstPriceObject.currency_code,
        exchangeRateProvider,
      )) as number;
    }

    if (currency === domesticCurrency) {
      this.refreshPrice(domesticPrice);

      return;
    }

    this.refreshPrice(
      FormHelper.toSourceCurrency(
        domesticPrice,
        currency,
        exchangeRateProvider,
      ) as number,
    );

    this.setOriginalPrice(
      FormHelper.toSourceCurrency(
        this.calculateOriginalPrice(
          domesticCurrency,
          useRepayment,
          repaymentCount,
        ) ?? 0,
        currency,
        exchangeRateProvider,
      ) as number,
    );

    this.updateIsSpecialPrice(domesticCurrency, useRepayment);
  }

  private refreshPrice(price: number) {
    if (
      this.includingVat &&
      this.currentVat === 0.0 &&
      this._vatPayer === false
    ) {
      const oldCoefficient = this.originVat / (100 + this.originVat);

      price = NumberHelper.round(price * (1 - oldCoefficient));
    }

    this._currentPriceIncVat = NumberHelper.round(
      NumberHelper.priceIncludingVat(price, this.currentVat, this.includingVat),
    );

    this._currentPrice = NumberHelper.round(
      NumberHelper.priceWithOutVat(price, this.currentVat, this.includingVat),
    );
  }

  get isOneStopShopItemType(): boolean {
    return (
      this.type === ItemType.GOOD ||
      this.type === ItemType.FOOD ||
      this.type === ItemType.PRINTED_PUBLICATION ||
      this.type === ItemType.PRINTED_PERIOD_PUBLICATION ||
      this.isElectronicallySuppliedService
    );
  }

  get isLiveEventItemType(): boolean {
    return this.type === ItemType.LIVE_EVENT;
  }

  get isElectronicallySuppliedService(): boolean {
    return (
      this.type === ItemType.ELECTRONIC_PERIOD_PUBLICATION ||
      this.type === ItemType.ELECTRONIC_PUBLICATION ||
      this.type === ItemType.ELECTRONIC_SERVICE
    );
  }

  get isPhysicalItem(): boolean {
    return (
      this.type === ItemType.GOOD ||
      this.type === ItemType.PRINTED_PUBLICATION ||
      this.type === ItemType.PRINTED_PERIOD_PUBLICATION ||
      this.type === ItemType.FOOD
    );
  }

  get currentPrice(): number {
    return this._currentPrice;
  }

  get currentPriceIncVat(): number {
    return this._currentPriceIncVat;
  }

  get currentVat(): number {
    return this._currentVat;
  }

  get currentDiscountedPriceIncVat(): number | null {
    return this._currentDiscountedPriceIncVat;
  }

  get currentDiscountedPrice(): number | null {
    return this._currentDiscountedPrice;
  }

  get originalPriceIncVat(): number {
    return this._originalPriceIncVat;
  }

  get originalPrice(): number {
    return this._originalPrice;
  }

  get isSpecialPrice(): boolean {
    return this._isSpecialPrice;
  }

  resetDiscount() {
    this._currentDiscountedPriceIncVat = null;
    this._currentDiscountedPrice = null;
  }

  applyDiscountCode(discountCode: DiscountCode) {
    if (this.disableDiscount) {
      return;
    }

    const discountCoefficient = NumberHelper.discountCoefficient(
      discountCode.getPercentDiscount(),
    );

    this._currentDiscountedPriceIncVat = NumberHelper.round(
      this._currentPriceIncVat * discountCoefficient,
    );

    this._currentDiscountedPrice = NumberHelper.round(
      this._currentPrice * discountCoefficient,
    );
  }

  applyRepayment(repaymentCount: number) {
    this._currentPrice /= repaymentCount;
    this._currentPriceIncVat /= repaymentCount;

    if (this._currentDiscountedPriceIncVat !== null) {
      this._currentDiscountedPriceIncVat /= repaymentCount;
    }

    if (this._currentDiscountedPrice !== null) {
      this._currentDiscountedPrice /= repaymentCount;
    }
  }

  get isDisableRepayment(): boolean {
    return this.metadata?.is_disable_repayment ?? false;
  }

  updateOriginalPrice(
    currency: string,
    useRepayment: boolean,
    repaymentCount: number | null,
  ) {
    const price = this.calculateOriginalPrice(
      currency,
      useRepayment,
      repaymentCount,
    );

    if (price !== undefined) {
      this.setOriginalPrice(price);
    }
  }

  calculateOriginalPrice(
    currency: string,
    useRepayment: boolean,
    repaymentCount: number | null,
  ): number | undefined {
    return this.findPriceByCurrencyAndType(
      currency,
      useRepayment,
      repaymentCount,
      false,
    )?.price;
  }

  setOriginalPrice(price: number) {
    if (price === 0) {
      return;
    }

    this._originalPrice = NumberHelper.priceWithOutVat(
      price,
      this.currentVat,
      this.includingVat,
    );

    this._originalPriceIncVat = NumberHelper.priceIncludingVat(
      price,
      this.currentVat,
      this.includingVat,
    );
  }

  updateIsSpecialPrice(currency: string, useRepayment: boolean) {
    if (useRepayment) {
      this._isSpecialPrice = false;
      return;
    }

    const specialPrice = this.prices.find(
      (ele) =>
        ele.currency_code === currency && ele.type === "special_one_time",
    );

    if (specialPrice === undefined) {
      this._isSpecialPrice = false;
      return;
    }

    this._isSpecialPrice = specialPrice.isSpecialPriceDateInRange();
  }

  private findPriceByCurrencyAndType(
    currency: string,
    useRepayment: boolean,
    repaymentCount: number | null = null,
    useSpecial: boolean = true,
  ): FormItemPrice | undefined {
    if (useRepayment && repaymentCount !== null) {
      for (let count = repaymentCount; count > 1; count--) {
        const repaymentPrice = this.prices.find(
          (ele) =>
            ele.currency_code === currency &&
            ele.type === "repayment" &&
            ele.repayment_count === count,
        );

        if (repaymentPrice) {
          return repaymentPrice;
        }
      }

      const repaymentPrice = this.prices.find(
        (ele) =>
          ele.currency_code === currency &&
          ele.type === "repayment" &&
          ele.repayment_count === null,
      );

      if (repaymentPrice) {
        return repaymentPrice;
      }
    } else if (useSpecial) {
      const specialPrice = this.prices.find(
        (ele) =>
          ele.currency_code === currency && ele.type === "special_one_time",
      );

      if (specialPrice && specialPrice.isSpecialPriceDateInRange()) {
        return specialPrice;
      }
    }

    return this.prices.find(
      (ele) => ele.currency_code === currency && ele.type === "one_time",
    );
  }
}
